import React from 'react';
import { Play, Sparkles, Zap, Shield } from 'lucide-react';

const translations = {
  de: {
    welcome: 'Willkommen bei',
    brand: 'weltwelt365',
    tagline: 'wo jeder etwas für sich finden kann.',
    description: 'Jeder abonnierte Nutzer erhält unbegrenzten Zugang zur Plattform und kann die Inhalte rund um die Uhr genießen. Unsere Kunden können aus mehr als 90 lustigen Spielen wählen. Wir garantieren eine benutzerfreundliche Oberfläche, keine Installation - HTML5 Inhalte und keine Werbung. Dies sind nur einige der Vorteile unseres Dienstes.',
    cta: 'Entdecke unsere Spiele',
    explore: 'Erforschen Sie den Rest!',
    stats: {
      games: '90+ Spiele',
      users: '24/7 Zugang',
      noAds: 'Keine Werbung',
    }
  },
  en: {
    welcome: 'Welcome to',
    brand: 'weltwelt365',
    tagline: 'where everyone can find something for themselves.',
    description: 'Every subscribed user gets unlimited access to the platform and can enjoy the content around the clock. Our customers can choose from more than 90 fun games. We guarantee a user-friendly interface, no installation - HTML5 content and no advertising. These are just some of the benefits of our service.',
    cta: 'Discover our Games',
    explore: 'Explore the rest!',
    stats: {
      games: '90+ Games',
      users: '24/7 Access',
      noAds: 'No Ads',
    }
  }
};

function Hero({ language }) {
  const t = translations[language];

  const scrollToGames = () => {
    const element = document.getElementById('games');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
        style={{ backgroundImage: 'url(/og-image.png)' }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-darker/80 via-darker/60 to-darker"></div>
      
      {/* Background Elements */}
      <div className="absolute inset-0 grid-pattern"></div>
      
      {/* Floating geometric shapes */}
      <div className="absolute top-32 left-10 w-20 h-20 border-2 border-primary/30 rotate-45 animate-float"></div>
      <div className="absolute top-48 right-20 w-16 h-16 border-2 border-accent/30 rounded-full animate-float animation-delay-400"></div>
      <div className="absolute bottom-32 left-1/4 w-12 h-12 bg-secondary/20 rotate-12 animate-float animation-delay-600"></div>
      <div className="absolute bottom-48 right-1/3 w-8 h-8 border-2 border-neon/40 animate-float animation-delay-200"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-accent text-sm font-body font-semibold tracking-wide">PREMIUM GAMING PORTAL</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              {t.welcome}
              <span className="block gradient-text text-5xl sm:text-6xl lg:text-7xl mt-2">{t.brand}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-accent font-body font-medium mb-6">
              {t.tagline}
            </p>
            
            <p className="text-slate-400 text-lg font-body leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {t.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
              <button 
                onClick={scrollToGames}
                className="btn-glow group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] hover:bg-right text-white font-body font-bold text-lg rounded-xl transition-all duration-500"
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {t.cta}
              </button>
              <button className="flex items-center justify-center gap-3 px-8 py-4 border-2 border-slate-600 hover:border-accent text-white font-body font-semibold text-lg rounded-xl transition-all duration-300 hover:bg-accent/10">
                <Zap className="w-5 h-5 text-accent" />
                {t.explore}
              </button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-8">
              {[
                { icon: Sparkles, label: t.stats.games, color: 'text-primary' },
                { icon: Zap, label: t.stats.users, color: 'text-accent' },
                { icon: Shield, label: t.stats.noAds, color: 'text-secondary' },
              ].map((stat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-slate-800/50 ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <span className="text-white font-body font-semibold">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Element - Main Picture */}
          <div className="relative hidden lg:block">
            <div className="relative z-10">
              {/* Main image container with glow effect */}
              <div className="relative w-full max-w-lg mx-auto">
                <div className="glow-border rounded-3xl p-1">
                  <div className="bg-surface rounded-3xl overflow-hidden relative">
                    <img 
                      src="/og-image.png" 
                      alt="Mobile Gaming Portal"
                      className="w-full h-auto object-cover rounded-2xl transform hover:scale-105 transition-transform duration-500"
                    />
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-darker/60 via-transparent to-transparent rounded-2xl"></div>
                  </div>
                </div>
                
                {/* Floating badge elements */}
                <div className="absolute -top-4 -right-4 px-4 py-2 bg-gradient-to-br from-accent to-neon rounded-xl shadow-neon-cyan animate-float">
                  <span className="text-white font-heading font-bold text-sm">90+ Games</span>
                </div>
                <div className="absolute -bottom-4 -left-4 px-4 py-2 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-neon animate-float animation-delay-400">
                  <span className="text-white font-heading font-bold text-sm">No Ads</span>
                </div>
              </div>
            </div>
            
            {/* Background glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/30 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-accent rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
